/**
 * HTML Sanitization utilities
 * Provides safe HTML sanitization using DOMPurify
 *
 * Usage:
 *   const cleanHTML = sanitizeHTML(userContent);
 *   const cleanText = sanitizeText(userInput);
 */

import DOMPurify from 'dompurify';

/**
 * Type definition for DOMPurify configuration
 */
type SanitizeConfig = {
  ALLOWED_TAGS?: string[];
  ALLOWED_ATTR?: string[];
  KEEP_CONTENT?: boolean;
  [key: string]: unknown;
};

/**
 * Configuration for DOMPurify
 * Restricts allowed HTML tags and attributes for security
 */
const SANITIZE_CONFIG: SanitizeConfig = {
  ALLOWED_TAGS: [
    'b',
    'i',
    'em',
    'strong',
    'u',
    'br',
    'p',
    'a',
    'ul',
    'ol',
    'li',
    'blockquote',
    'code',
    'pre',
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6',
  ],
  ALLOWED_ATTR: ['href', 'title', 'target', 'rel'],
  KEEP_CONTENT: true,
};

/**
 * Sanitizes HTML content using DOMPurify
 * Removes potentially dangerous HTML/JS while preserving safe markup
 *
 * @param dirty - Raw HTML string that may contain malicious content
 * @param config - Optional custom DOMPurify configuration
 * @returns Sanitized HTML string safe to render
 *
 * @example
 * const userBio = '<p>Hello <script>alert("xss")</script></p>';
 * const safe = sanitizeHTML(userBio);
 * // Result: '<p>Hello </p>'
 */
export function sanitizeHTML(
  dirty: string,
  config: SanitizeConfig = SANITIZE_CONFIG
): string {
  if (!dirty || typeof dirty !== 'string') {
    return '';
  }

  return DOMPurify.sanitize(dirty, config);
}

/**
 * Sanitizes text by escaping all HTML characters
 * Use this when you need to display user input as plain text
 *
 * @param text - Raw text that may contain HTML characters
 * @returns Escaped text safe to render
 *
 * @example
 * const userInput = '<img src=x onerror=alert("xss")>';
 * const safe = sanitizeText(userInput);
 * // Result: '&lt;img src=x onerror=alert(&quot;xss&quot;)&gt;'
 */
export function sanitizeText(text: string): string {
  if (!text || typeof text !== 'string') {
    return '';
  }

  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

/**
 * Sanitizes URLs to prevent javascript: and data: schemes
 * Safe for use in href, src attributes
 *
 * @param url - URL that may contain malicious schemes
 * @returns Safe URL or empty string
 *
 * @example
 * sanitizeURL('javascript:alert("xss")') // Returns ''
 * sanitizeURL('https://example.com') // Returns 'https://example.com'
 */
export function sanitizeURL(url: string): string {
  if (!url || typeof url !== 'string') {
    return '';
  }

  // Remove whitespace
  const trimmed = url.trim().toLowerCase();

  // Block dangerous protocols
  const dangerousProtocols = ['javascript:', 'data:', 'vbscript:'];
  if (dangerousProtocols.some((proto) => trimmed.startsWith(proto))) {
    return '';
  }

  // Allow relative URLs starting with /
  if (trimmed.startsWith('/')) {
    return url;
  }

  // Allow safe protocols
  const safeProtocols = ['http://', 'https://', 'mailto:', 'tel:'];
  if (safeProtocols.some((proto) => trimmed.startsWith(proto))) {
    return url;
  }

  // Default to empty for unknown protocols
  return '';
}

/**
 * Creates a trusted HTML policy for use with Trusted Types API
 * Validates HTML before creating TrustedHTML objects
 *
 * @param html - HTML string to validate and create policy for
 * @returns Sanitized HTML safe for DOM operations
 *
 * @example
 * const policy = createTrustedHTMLPolicy(userHTML);
 * element.innerHTML = policy; // Safe assignment
 */
export function createTrustedHTMLPolicy(
  html: string
): ReturnType<typeof sanitizeHTML> {
  return sanitizeHTML(html);
}

/**
 * Checks if content appears to contain potentially malicious patterns
 * Useful for logging/monitoring security events
 *
 * @param content - Content to check
 * @returns true if suspicious patterns detected
 *
 * @example
 * if (isSuspiciousContent(userInput)) {
 *   logger.warn('Potential XSS attempt detected');
 * }
 */
export function isSuspiciousContent(content: string): boolean {
  if (!content || typeof content !== 'string') {
    return false;
  }

  const suspiciousPatterns = [
    /<script[^>]*>[\s\S]*?<\/script>/gi,
    /on\w+\s*=\s*["'][^"']*["']/gi, // Event handlers
    /javascript:/gi,
    /data:text\/html/gi,
  ];

  return suspiciousPatterns.some((pattern) => pattern.test(content));
}

export default {
  sanitizeHTML,
  sanitizeText,
  sanitizeURL,
  createTrustedHTMLPolicy,
  isSuspiciousContent,
};
